import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { AlertCircle } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/features/authentication/components/auth-provider'
import { isAdminUser, isOrganisationAdminUser } from '@/lib/role-guards'
import { Textarea } from '@/components/ui/textarea'
import { toast } from 'sonner'
import {
  useBulkCreateTrainingNeeds,
  useSubmitTrainingNeed
} from '@/utils/api/membership-service'
import { useGetAllOrganisations } from '@/utils/api/user-service'
import type { OrganisationPayload } from '@/utils/api/user-service/models/organisation-payload'
import { buildTrainingNeedsParams, type DateRangePreset, getDateRangeBounds } from './utils'

import { useTrainingNeedsQuery } from './api/useTrainingNeedsQuery'
import { TrainingNeedsTable } from './components/TrainingNeedsTable'
import { OrganisationSelect } from './components/OrganisationSelect'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type OrganisationOption = { id: number; name: string }

const DEFAULT_DATE_PRESET: DateRangePreset = '30d'

interface FilterState {
  organisationId: string
  organisationName: string
  topic: string
  datePreset: DateRangePreset
  from: string
  to: string
}

const DEFAULT_PAGE_SIZE = 10

export function TrainingNeedsPage() {
  const { user } = useAuth()
  const isAdmin = isAdminUser(user)
  const isOrganisationAdmin = isOrganisationAdminUser(user)
  const organisationName =
    user?.mainOrgainisation ??
    (Array.isArray(user?.organisations) && user.organisations.length > 0
      ? user.organisations[0] ?? ''
      : '')
  const organisationIdFromUser =
    typeof user?.mainOrgainisationId === 'number' ? user.mainOrgainisationId : null
  const defaultOrganisationId = organisationIdFromUser ? String(organisationIdFromUser) : ''

  if (!isAdmin && !isOrganisationAdmin) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Access denied</AlertTitle>
        <AlertDescription>
          You need administrator permissions to view training needs submissions.
        </AlertDescription>
      </Alert>
    )
  }

  if (isOrganisationAdmin && !organisationIdFromUser) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Organisation details missing</AlertTitle>
        <AlertDescription>
          We could not determine the organisation linked to your account. Contact support to have your
          organisation configured before viewing training needs submissions.
        </AlertDescription>
      </Alert>
    )
  }

  const defaultDateRange = useMemo(() => getDateRangeBounds(DEFAULT_DATE_PRESET), [])

  const [tablePagination, setTablePagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })
  const [selectedOrganisationId, setSelectedOrganisationId] = useState<string>(
    isAdmin ? '' : defaultOrganisationId,
  )
  const [datePreset, setDatePreset] = useState<DateRangePreset>(DEFAULT_DATE_PRESET)
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [appliedFilters, setAppliedFilters] = useState<FilterState>({
    organisationId: isAdmin ? '' : defaultOrganisationId,
    organisationName: isAdmin ? '' : organisationName?.trim() ?? '',
    topic: '',
    datePreset: DEFAULT_DATE_PRESET,
    from: defaultDateRange.from,
    to: defaultDateRange.to,
  })
  const [tableResetCounter, setTableResetCounter] = useState(0)
  const [topicFilterInput, setTopicFilterInput] = useState('')
  const [topicInput, setTopicInput] = useState('')
  const [topicError, setTopicError] = useState<string | null>(null)
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (datePreset !== 'custom') {
      setCustomFrom('')
      setCustomTo('')
    }
  }, [datePreset])

  const organisationsQuery = useGetAllOrganisations<OrganisationPayload[]>({
    query: {
      enabled: isAdmin,
      select: (response) => response.data ?? [],
    },
  })

  const organisationOptions = useMemo<OrganisationOption[]>(() => {
    if (isAdmin) {
      const data = organisationsQuery.data ?? []
      return data
        .map((org) => {
          const id = typeof org.id === 'number' ? org.id : null
          const name = org.name?.trim()
          if (!id || !name) return null
          return { id, name }
        })
        .filter((entry): entry is OrganisationOption => Boolean(entry))
    }

    if (organisationIdFromUser) {
      const name =
        organisationName?.trim() ||
        organisationsQuery.data?.find((org) => org.id === organisationIdFromUser)?.name?.trim() ||
        user?.mainOrgainisation?.trim() ||
        'My organisation'
      return [{ id: organisationIdFromUser, name }]
    }

    return []
  }, [
    isAdmin,
    organisationsQuery.data,
    organisationIdFromUser,
    organisationName,
    user?.mainOrgainisation,
  ])

  const organisationLookup = useMemo(() => {
    const map = new Map<number, OrganisationOption>()
    organisationOptions.forEach((org) => {
      map.set(org.id, org)
    })
    return map
  }, [organisationOptions])

  const organisationNameLookup = useMemo(() => {
    const map = new Map<string, OrganisationOption>()
    organisationOptions.forEach((org) => {
      map.set(org.name.toLowerCase(), org)
    })
    return map
  }, [organisationOptions])

useEffect(() => {
  if (!isAdmin) {
    const initialOrganisation =
      organisationOptions.find((option) => String(option.id) === defaultOrganisationId) ?? organisationOptions[0]

    if (initialOrganisation) {
      setSelectedOrganisationId((prev) => prev || String(initialOrganisation.id))
      setAppliedFilters((prev) => {
        if (
          prev.organisationId === String(initialOrganisation.id)
          && prev.organisationName === initialOrganisation.name
        ) {
          return prev
        }

        return {
          ...prev,
          organisationId: String(initialOrganisation.id),
          organisationName: initialOrganisation.name,
        }
      })
    }
  }
}, [isAdmin, organisationOptions, defaultOrganisationId])

  useEffect(() => {
    if (isAdmin && selectedOrganisationId) {
      const numericId = Number(selectedOrganisationId)
      if (!Number.isFinite(numericId) || !organisationLookup.has(numericId)) {
        setSelectedOrganisationId('')
      }
    }
  }, [isAdmin, organisationLookup, selectedOrganisationId])

  const resolveOrganisation = (useAppliedFallback: boolean): OrganisationOption | null => {
    if (isAdmin) {
      const idString = selectedOrganisationId || (useAppliedFallback ? appliedFilters.organisationId : '')
      if (!idString) return null
      const numericId = Number(idString)
      if (!Number.isFinite(numericId)) return null
      return organisationLookup.get(numericId) ?? null
    }

    const baseIdString = useAppliedFallback
      ? appliedFilters.organisationId || defaultOrganisationId
      : selectedOrganisationId || defaultOrganisationId

    const numericId = Number(baseIdString)
    if (Number.isFinite(numericId) && numericId > 0) {
      const existing = organisationLookup.get(numericId)
      if (existing) {
        return existing
      }
      const fallbackName =
        appliedFilters.organisationName || organisationName?.trim() || organisationOptions[0]?.name || 'My organisation'
      return { id: numericId, name: fallbackName }
    }

    return null
  }

  const activeOrganisation = resolveOrganisation(false)

  const handleApplyFilters = () => {
    const organisationForFilters = resolveOrganisation(false)
    const organisationNameForFilters = organisationForFilters?.name ?? (isAdmin ? '' : organisationName?.trim() ?? '')

    let from = ''
    let to = ''

    if (datePreset === 'custom') {
      if (!customFrom || !customTo) {
        toast.error('Select custom dates', {
          description: 'Provide both start and end dates for the custom range.',
        })
        return
      }

      if (new Date(customFrom) > new Date(customTo)) {
        toast.error('Invalid date range', {
          description: 'The start date must be before the end date.',
        })
        return
      }

      from = customFrom
      to = customTo
    } else {
      const bounds = getDateRangeBounds(datePreset)
      from = bounds.from
      to = bounds.to
    }

    setAppliedFilters({
      organisationId: isAdmin ? selectedOrganisationId : defaultOrganisationId,
      organisationName: organisationNameForFilters,
      topic: isAdmin ? topicFilterInput.trim() : '',
      datePreset,
      from,
      to,
    })
    setTablePagination((prev) => ({
      pageIndex: 0,
      pageSize: prev.pageSize,
    }))
    setTableResetCounter((prev) => prev + 1)
  }

  const handleResetFilters = () => {
    if (isAdmin) {
      setSelectedOrganisationId('')
      setTopicFilterInput('')
    }
    setDatePreset(DEFAULT_DATE_PRESET)
    setCustomFrom('')
    setCustomTo('')

    const fallbackOrganisation = resolveOrganisation(true)

    setAppliedFilters({
      organisationId: isAdmin ? '' : defaultOrganisationId,
      organisationName: fallbackOrganisation?.name ?? (isAdmin ? '' : organisationName?.trim() ?? ''),
      topic: '',
      datePreset: DEFAULT_DATE_PRESET,
      from: defaultDateRange.from,
      to: defaultDateRange.to,
    })
    setTablePagination({
      pageIndex: 0,
      pageSize: DEFAULT_PAGE_SIZE,
    })
    setTableResetCounter((prev) => prev + 1)
  }

  const queryParams = useMemo(
    () =>
      buildTrainingNeedsParams({
        pageIndex: tablePagination.pageIndex,
        pageSize: tablePagination.pageSize,
        organisationName: appliedFilters.organisationName,
        topic: appliedFilters.topic,
        dateFrom: appliedFilters.from,
        dateTo: appliedFilters.to,
        isAdmin,
        isOrganisationAdmin,
      }),
    [
      appliedFilters.organisationName,
      appliedFilters.topic,
      appliedFilters.from,
      appliedFilters.to,
      isAdmin,
      isOrganisationAdmin,
      tablePagination.pageIndex,
      tablePagination.pageSize,
    ],
  )

  const queryEnabled = Boolean(queryParams)

  const trainingNeedsQuery = useTrainingNeedsQuery(queryEnabled ? queryParams : undefined, {
    enabled: queryEnabled,
  })

  const createTrainingNeedMutation = useSubmitTrainingNeed({
    mutation: {
      onSuccess: async () => {
        toast.success('Training need submitted', {
          description: 'Your organisation request has been recorded.',
        })
        setTopicInput('')
        setTopicError(null)
        setSubmitDialogOpen(false)
        await trainingNeedsQuery.refetch()
      },
      onError: (error) => {
        const message =
          error instanceof Error
            ? error.message
            : 'We could not submit your training need. Please try again.'
        toast.error('Submission failed', { description: message })
      },
    },
  })

  const bulkTrainingNeedMutation = useBulkCreateTrainingNeeds({
    mutation: {
      onSuccess: async () => {
        toast.success('Training needs uploaded', {
          description: 'Your template upload has been processed.',
        })
        await trainingNeedsQuery.refetch()
      },
      onError: (error) => {
        const message =
          error instanceof Error ? error.message : 'We could not upload the template. Please try again.'
        toast.error('Upload failed', { description: message })
      },
    },
  })

  const records = trainingNeedsQuery.data?.items ?? []
  const totalCount = trainingNeedsQuery.data?.totalElements ?? 0
  const isLoading = trainingNeedsQuery.isPending || trainingNeedsQuery.isFetching
  const queryError =
    trainingNeedsQuery.isError
      ? trainingNeedsQuery.error instanceof Error
        ? trainingNeedsQuery.error
        : new Error('Unable to load training needs. Please try again later.')
      : null
  const showEmpty = !isLoading && !queryError && totalCount === 0
  const isDefaultOrganisationSelection = isAdmin ? selectedOrganisationId === '' : true
  const isDefaultTopicInput = isAdmin ? topicFilterInput.trim().length === 0 : true
  const isDefaultDateSelection =
    datePreset === DEFAULT_DATE_PRESET
    && customFrom.length === 0
    && customTo.length === 0

  const resetDisabled =
    isDefaultOrganisationSelection && isDefaultTopicInput && isDefaultDateSelection

  const handlePageChange = (pageIndex: number, pageSize: number) => {
    setTablePagination((prev) => {
      if (prev.pageIndex === pageIndex && prev.pageSize === pageSize) {
        return prev
      }

      return { pageIndex, pageSize }
    })
  }

  const handlePageSizeChange = (pageSize: number) => {
    setTablePagination((prev) => {
      if (prev.pageSize === pageSize && prev.pageIndex === 0) {
        return prev
      }
      return {
        pageIndex: 0,
        pageSize,
      }
    })
    setTableResetCounter((prev) => prev + 1)
  }

  const handleTrainingNeedSubmit = async () => {
    const trimmedTopic = topicInput.trim()
    if (trimmedTopic.length === 0) {
      setTopicError('Enter the training topic or objective.')
      return
    }
    if (trimmedTopic.length > 300) {
      setTopicError('Topics should be within 300 characters.')
      return
    }

    const targetOrganisation = resolveOrganisation(false)
    if (!targetOrganisation) {
      toast.error('Select an organisation', {
        description: 'Choose an organisation before submitting a training need.',
      })
      return
    }

    setTopicError(null)

    try {
      await createTrainingNeedMutation.mutateAsync({
        data: {
          organisationName: targetOrganisation.name,
          topic: trimmedTopic,
        },
      })
    } catch {
      /* handled in onError */
    }
  }

  const canSubmitTopic =
    Boolean(activeOrganisation) &&
    topicInput.trim().length > 0 &&
    !createTrainingNeedMutation.isPending

  const escapeCsvValue = (value: string) => {
    if (/[",\n]/.test(value)) {
      return `"${value.replace(/"/g, '""')}"`
    }
    return value
  }

  const handleTemplateDownload = () => {
    const templateOrganisation = resolveOrganisation(true)
    if (!templateOrganisation) {
      toast.error('Select an organisation', {
        description: 'Choose an organisation before downloading the template.',
      })
      return
    }
    const sampleOrgName = escapeCsvValue(templateOrganisation.name)
    const sampleTopic = 'Financial literacy workshop for branch teams'
    const csvContent = `organisation,topic\n${sampleOrgName},${escapeCsvValue(sampleTopic)}`
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'training-need-template.csv'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const splitCsvLine = (line: string) => {
    const cells: string[] = []
    let current = ''
    let inQuotes = false

    for (let index = 0; index < line.length; index += 1) {
      const char = line[index]
      if (char === '"') {
        if (inQuotes && line[index + 1] === '"') {
          current += '"'
          index += 1
        } else {
          inQuotes = !inQuotes
        }
      } else if (char === ',' && !inQuotes) {
        cells.push(current.trim())
        current = ''
      } else {
        current += char
      }
    }

    cells.push(current.trim())

    return cells.map((value) => value.replace(/^"(.*)"$/, '$1').trim())
  }

  const handleTemplateUpload = (file: File) => {
    const reader = new FileReader()
    reader.onload = async () => {
      const text = typeof reader.result === 'string' ? reader.result : ''
      const lines = text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0)

      if (lines.length === 0) {
        toast.error('Template empty', {
          description: 'Add at least one training topic before uploading.',
        })
        return
      }

      const headerCells = splitCsvLine(lines[0])
      const organisationIdIndex = headerCells.findIndex(
        (cell) => cell.toLowerCase() === 'organisationid',
      )
      const organisationNameIndex = headerCells.findIndex(
        (cell) => cell.toLowerCase() === 'organisation',
      )
      const topicIndex = headerCells.findIndex(
        (cell) => cell.toLowerCase() === 'topic',
      )

      if (topicIndex === -1) {
        toast.error('Missing topic column', {
          description: 'Ensure the template includes a "topic" column.',
        })
        return
      }

      const topics: string[] = []
      let organisationNameForFile: string | null = null

      for (const line of lines.slice(1)) {
        const cells = splitCsvLine(line)
        const topic = cells[topicIndex]?.trim()
        if (!topic) {
          continue
        }

        let rowOrganisation: OrganisationOption | null = null
        if (organisationNameIndex !== -1) {
          const rawName = cells[organisationNameIndex]
          if (rawName) {
            const resolved = organisationNameLookup.get(rawName.trim().toLowerCase())
            if (!resolved) {
              toast.error('Unknown organisation', {
                description: `We could not match organisation "${rawName}" to your accessible organisations.`,
              })
              return
            }
            rowOrganisation = resolved
          }
        }

        if (organisationIdIndex !== -1) {
          const rawId = cells[organisationIdIndex]
          if (rawId) {
            const parsedId = Number(rawId)
            if (!Number.isFinite(parsedId) || parsedId <= 0) {
              toast.error('Invalid organisation ID', {
                description: `Check the organisationId for row "${line}".`,
              })
              return
            }
            const resolved = organisationLookup.get(parsedId)
            if (!resolved) {
              toast.error('Unknown organisation', {
                description: `We could not match organisation ID ${parsedId} to your accessible organisations.`,
              })
              return
            }
            rowOrganisation = resolved
          }
        }

        if (!rowOrganisation) {
          const fallbackOrganisation = resolveOrganisation(true)
          if (!fallbackOrganisation) {
            toast.error('Organisation required', {
              description:
                'Include an organisation column or select an organisation before uploading.',
            })
            return
          }
          rowOrganisation = fallbackOrganisation
        }

        if (organisationNameForFile === null) {
          organisationNameForFile = rowOrganisation.name
        } else if (organisationNameForFile !== rowOrganisation.name) {
          toast.error('Multiple organisations detected', {
            description: 'Upload a separate template for each organisation.',
          })
          return
        }

        topics.push(topic)
      }

      if (topics.length === 0) {
        toast.error('Template empty', {
          description: 'Add at least one training topic before uploading.',
        })
        return
      }

      if (!organisationNameForFile) {
        toast.error('Organisation required', {
          description:
            'Include an organisation column or select an organisation before uploading.',
        })
        return
      }

      const organisationNameCheck = resolveOrganisation(false)?.name ?? organisationName?.trim() ?? ''
      if (!isAdmin && organisationNameCheck && organisationNameForFile.trim().toLowerCase() !== organisationNameCheck.toLowerCase()) {
        toast.error('Organisation mismatch', {
          description: 'You can only upload training needs for your organisation.',
        })
        return
      }

      try {
        await bulkTrainingNeedMutation.mutateAsync({
          data: {
            organisationName: organisationNameForFile,
            topics,
          },
        })
      } catch {
        /* handled in onError */
      }
    }
    reader.onerror = () => {
      toast.error('Unable to read file', {
        description: 'Check the template and try again.',
      })
    }
    reader.readAsText(file)
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Training needs submissions</CardTitle>
          <CardDescription>
            {isAdmin
              ? ' Filter by organisation to focus on specific partners.'
              : ' This list shows all entries submitted under your organisation.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={handleTemplateDownload}>
              Download template
            </Button>
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={bulkTrainingNeedMutation.isPending}
            >
              {bulkTrainingNeedMutation.isPending ? 'Uploading…' : 'Upload template'}
            </Button>
            <Input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const file = event.target.files?.[0]
                if (file) {
                  handleTemplateUpload(file)
                  event.target.value = ''
                }
              }}
            />
            <div className="ml-auto flex gap-2">
              {isOrganisationAdmin || isAdmin ? (
                <Dialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>Submit training need</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Submit a new training need</DialogTitle>
                      <DialogDescription>
                        Outline the training support your organisation requires. Administrators will review
                        and incorporate it into upcoming programmes.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        {isAdmin ? (
                          <div className="space-y-2">
                            <Label>Organisation</Label>
                            <OrganisationSelect
                              value={selectedOrganisationId}
                              onChange={setSelectedOrganisationId}
                              organisations={organisationOptions}
                              isLoading={organisationsQuery.isPending}
                              placeholder="Select organisation…"
                            />
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            Organisation:&nbsp;
                            <strong className="text-foreground">
                              {activeOrganisation?.name ?? 'Unavailable'}
                            </strong>
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="training-need-topic">Training topic or objective</Label>
                        <Textarea
                          id="training-need-topic"
                          value={topicInput}
                          onChange={(event) => {
                            setTopicInput(event.target.value)
                            if (topicError) {
                              setTopicError(null)
                            }
                          }}
                          maxLength={300}
                          placeholder="e.g. Refresher workshop on AML/KYC compliance for frontline officers."
                          disabled={createTrainingNeedMutation.isPending}
                          rows={4}
                        />
                        {topicError ? <p className="text-sm text-destructive">{topicError}</p> : null}
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        onClick={() => {
                          void handleTrainingNeedSubmit()
                        }}
                        disabled={!canSubmitTopic}
                      >
                        {createTrainingNeedMutation.isPending ? 'Submitting…' : 'Submit training need'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              ) : null}
            </div>
          </div>

          <div className="flex flex-col gap-4 xl:flex-row xl:flex-wrap xl:items-end">
            {isAdmin ? (
              <div className="flex min-w-[220px] flex-1 flex-col gap-2">
                <Label>Organisation</Label>
                <OrganisationSelect
                  value={selectedOrganisationId}
                  onChange={setSelectedOrganisationId}
                  organisations={organisationOptions}
                  isLoading={organisationsQuery.isPending}
                  allowClear
                  placeholder="Select organisation…"
                />
              </div>
            ) : null}

            {isAdmin ? (
              <div className="flex min-w-[220px] flex-1 flex-col gap-2">
                <Label htmlFor="topic-filter">Filter by topic</Label>
                <Input
                  id="topic-filter"
                  value={topicFilterInput}
                  onChange={(event) => setTopicFilterInput(event.target.value)}
                  placeholder="e.g. Compliance refresher"
                />
              </div>
            ) : null}

            <div className="flex min-w-[200px] flex-1 flex-col gap-2">
              <Label htmlFor="training-date-range">Date range</Label>
              <Select
                value={datePreset}
                onValueChange={(value) => setDatePreset(value as DateRangePreset)}
              >
                <SelectTrigger id="training-date-range" className="w-full">
                  <SelectValue placeholder="Select range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="90d">Last 90 days</SelectItem>
                  <SelectItem value="6m">Last 6 months</SelectItem>
                  <SelectItem value="ytd">Year to date</SelectItem>
                  <SelectItem value="custom">Custom range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {datePreset === 'custom' ? (
              <div className="flex min-w-[180px] flex-1 flex-col gap-2">
                <Label htmlFor="custom-from">From</Label>
                <Input
                  id="custom-from"
                  type="date"
                  value={customFrom}
                  onChange={(event) => setCustomFrom(event.target.value)}
                  max={customTo || undefined}
                />
              </div>
            ) : null}

            {datePreset === 'custom' ? (
              <div className="flex min-w-[180px] flex-1 flex-col gap-2">
                <Label htmlFor="custom-to">To</Label>
                <Input
                  id="custom-to"
                  type="date"
                  value={customTo}
                  onChange={(event) => setCustomTo(event.target.value)}
                  min={customFrom || undefined}
                />
              </div>
            ) : null}

            <div className="flex gap-2">
              <Button onClick={handleApplyFilters}>Apply filters</Button>
              <Button variant="outline" onClick={handleResetFilters} disabled={resetDisabled}>
                Reset
              </Button>
            </div>
          </div>

          {queryError ? (
            <Alert variant="destructive">
              <AlertTitle>Unable to load training needs</AlertTitle>
              <AlertDescription className="flex items-center justify-between gap-2">
                <span>{queryError.message}</span>
                <Button variant="outline" size="sm" onClick={() => trainingNeedsQuery.refetch()}>
                  Try again
                </Button>
              </AlertDescription>
            </Alert>
          ) : showEmpty ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>No submissions yet</AlertTitle>
              <AlertDescription>
                {isAdmin
                  ? 'No training needs have been submitted that match the current filters.'
                  : 'Your organisation has not submitted any training needs yet.'}
              </AlertDescription>
            </Alert>
          ) : (
            <TrainingNeedsTable
              records={records}
              totalCount={totalCount}
              pageSize={tablePagination.pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              loading={isLoading}
              stateKey={tableResetCounter}
              searchPlaceholder="Search training needs…"
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
